import { useMutation, useQuery } from "react-query";
import {
    generateSurveyCode,
	getSectionQuestions,
	getSurveyCode,
	getSurveySectionById,
	getSurveySections,
	initiateSurveySection,
	sendAishaSms,
	submitSurvey,
	submitUATSurvey,
	validateLongSurveyCode,
	validateSurveyCode,
} from "../services/mis-survey-service";
import {
	submitPPMVSurvey,
	uploadToCloudinary,
} from "api/services/ppmv-service";

export const useValidateCode = (survey_code, shouldValidate) => {
	const response = useQuery({
		queryKey: [`survey-code-${survey_code}`],
		queryFn: () => validateSurveyCode(survey_code),
		enabled: shouldValidate,
	});

	return {
		isSuccess: response.isSuccess,
		isError: response.isError,
		isLoading: response.isLoading,
		data: response.data,
		error: response.error,
	};
};

export const useGetSurveySections = (survey_id, shouldValidate) => {
	const response = useQuery({
		queryKey: [`survey-sections-${survey_id}`],
		queryFn: () => getSurveySections(survey_id),
		enabled: shouldValidate,
	});

	return {
		isSuccess: response.isSuccess,
		isError: response.isError,
		isLoading: response.isLoading,
		data: response.data,
		error: response.error,
	};
};

export const useValidateSurveyCode = (options) => {
	const mutation = useMutation({
		mutationFn: (payload) => validateLongSurveyCode(payload),
		onSuccess: (data) => {
			options.onSuccess(data);
		},
		onError: (error) => {
			options.onError(error);
		},
	});

	return {
		validateCode: mutation.mutate,
		isSuccess: mutation.isSuccess,
		isError: mutation.isError,
		isLoading: mutation.isLoading,
	};
};

export const useGetSectionQuestions = (section_id) => {
	const response = useQuery({
		queryKey: [`survey-section-questions-${section_id}`],
		queryFn: () => getSectionQuestions(section_id),
	});

	return {
		isSuccess: response.isSuccess,
		isError: response.isError,
		isLoading: response.isLoading,
		data: response.data,
		error: response.error,
	};
};

export const useGetSectionById = (section_id) => {
	const response = useQuery({
		queryKey: [`survey-section-${section_id}`],
		queryFn: () => getSurveySectionById(section_id),
	});

	return {
		isSuccess: response.isSuccess,
		isError: response.isError,
		isLoading: response.isLoading,
		data: response.data,
		error: response.error,
	};
};

export const useInitiateSurveySection = (
	survey_code,
	phone_number,
	section_id,
	survey_id,
	enabled
) => {
	const response = useQuery({
		queryKey: [`initiate-survey-section-${survey_code}`],
		queryFn: () =>
			initiateSurveySection(
				survey_code,
				phone_number,
				survey_id,
				section_id
			),
		enabled: enabled,
	});

	return {
		isSuccess: response.isSuccess,
		isError: response.isError,
		isLoading: response.isLoading,
		data: response.data,
		error: response.error,
	};
};

export const useSubmitSurveyData = (options) => {
	const mutation = useMutation({
		mutationFn: (surveyFormData) => submitSurvey(surveyFormData),
		onSuccess: (data) => {
			options.onSuccess(data);
		},
		onError: (error) => {
			options.onError(error);
		},
	});

	return {
		submitSurvey: mutation.mutate,
		isSuccess: mutation.isSuccess,
		isError: mutation.isError,
		isLoading: mutation.isLoading,
	};
};

export const useSubmitUATSurveyData = (options) => {
	const mutation = useMutation({
		mutationFn: (surveyFormData) => submitUATSurvey(surveyFormData),
		onSuccess: (data) => {
			options.onSuccess(data);
		},
		onError: (error) => {
			options.onError(error);
		},
	});

	return {
		submitUATSurvey: mutation.mutate,
		isSuccess: mutation.isSuccess,
		isError: mutation.isError,
		isLoading: mutation.isLoading,
	};
};

export const useSubmitPPMVSurvey = (options) => {
	const mutation = useMutation({
		mutationFn: (surveyFormData) => submitPPMVSurvey(surveyFormData),
		onSuccess: (data) => {
			options.onSuccess(data);
		},
		onError: (error) => {
			options.onError(error);
		},
	});

	return {
		submitSurvey: mutation.mutate,
		isSuccess: mutation.isSuccess,
		isError: mutation.isError,
		isLoading: mutation.isLoading,
	};
};

export const useUploadFile = (options) => {
	const mutation = useMutation({
		mutationFn: (uploadData) =>
			uploadToCloudinary(uploadData.file, uploadData.folderPath),
		onSuccess: (data) => {
			if (!data) {
				options.onError("Error while uploading file");
				return;
			}
			options.onSuccess(data);
		},
		onError: (error) => {
			options.onError(error);
		},
	});

	return {
		uploadFile: mutation.mutate,
		isSuccess: mutation.isSuccess,
		isError: mutation.isError,
		isLoading: mutation.isLoading,
	};
};

export const useSendSms = (options) => {
	const mutation = useMutation({
		mutationFn: (surveyFormData) => sendAishaSms(surveyFormData),
		onSuccess: (data) => {
			options.onSuccess(data);
		},
		onError: (error) => {
			options.onError(error);
		},
	});

	return {
		sendSms: mutation.mutate,
		isSuccess: mutation.isSuccess,
		isError: mutation.isError,
		isLoading: mutation.isLoading,
	};
};

export const useGenerateSurveyCode = (options) => {
	const mutation = useMutation({
		mutationFn: (payload) => generateSurveyCode(payload),
		onSuccess: (data) => {
			options.onSuccess(data);
		},
		onError: (error) => {
			options.onError(error);
		},
	});

	return {
		generateCode: mutation.mutate,
		isSuccess: mutation.isSuccess,
		isError: mutation.isError,
		isLoading: mutation.isLoading,
	};
};

export const useGetSurveyCode = (phoneNumber, shouldValidate) => {
	const response = useQuery({
		queryKey: [`survey-code-number-${phoneNumber}`],
		queryFn: () => getSurveyCode(phoneNumber),
		enabled: shouldValidate,
	});

	return {
		isSuccess: response.isSuccess,
		isError: response.isError,
		isLoading: response.isLoading,
		data: response.data,
		error: response.error,
	};
};
